import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, ChevronRight, Sparkles, Layers, ShieldCheck, Cpu } from 'lucide-react';
import { STATS_HIGHLIGHTS, OPERATING_SYSTEM_STEPS } from '../data/siteData';

interface ResultsAndProcessProps {
  onOpenBooking: () => void;
}

export const ResultsAndProcess: React.FC<ResultsAndProcessProps> = ({ onOpenBooking }) => {
  const [activeStep, setActiveStep] = useState<number>(0);

  return (
    <>
      {/* Results / Outcomes Section */}
      <section className="py-20 lg:py-24 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl text-left mb-16">
            <div className="text-xs font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
              Results
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
              We don't ship websites. <br className="hidden sm:block" />
              We ship outcomes.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
              Every system we deploy is measured strictly against compounding pipeline, customer lifetime value, and organic market share.
            </p>
          </div>

          {/* Metric Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS_HIGHLIGHTS.map((stat, idx) => (
              <div
                key={stat.label}
                className="relative rounded-2xl bg-slate-50 border border-slate-200/80 p-6 sm:p-8 text-left hover:border-[#009fe3]/30 hover:bg-emerald-50/20 transition-all group"
              >
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 font-display tracking-tight tabular-nums group-hover:text-[#b88e4c] transition-colors">
                  {stat.value}
                </div>
                <div className="mt-2 text-base font-bold text-slate-900 font-display">
                  {stat.label}
                </div>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                  {stat.sub}
                </p>
                <div className="mt-4 h-1 w-12 rounded-full bg-slate-200 group-hover:w-full group-hover:bg-[#b88e4c] transition-all duration-300" />
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 6-Stage Operating System (Process) */}
      <section className="py-20 lg:py-28 bg-[#080d11] text-white border-b border-slate-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl text-left">
              <div className="text-xs font-bold uppercase tracking-widest text-[#009fe3] mb-2">
                Process
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-display">
                A six-stage operating system.
              </h2>
              <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed font-normal">
                Predictable execution from first diagnostic to continuous market compounding. No surprises, no black boxes.
              </p>
            </div>

            <button
              onClick={onOpenBooking}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b88e4c] hover:bg-[#009fe3] text-slate-950 font-bold px-5 py-3 text-xs transition-colors cursor-pointer self-start md:self-auto"
            >
              <span>Schedule Stage 01 Audit</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Process Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OPERATING_SYSTEM_STEPS.map((step, idx) => {
              const isSelected = activeStep === idx;
              return (
                <div
                  key={step.step}
                  onClick={() => setActiveStep(idx)}
                  className={`rounded-2xl p-6 sm:p-7 border transition-all duration-200 cursor-pointer text-left flex flex-col justify-between ${
                    isSelected
                      ? 'bg-slate-900 border-[#b88e4c]/80 shadow-lg shadow-emerald-500/10'
                      : 'bg-slate-950/70 border-slate-800/90 hover:border-slate-700 hover:bg-slate-900/60'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-xl font-extrabold text-[#009fe3]">
                        {step.step}
                      </span>
                      <span className="text-xs font-mono text-slate-500 tracking-wider uppercase">
                        Phase 0{idx + 1}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-white font-display mb-2">
                      {step.name}
                    </h3>
                    
                    <div className="text-xs font-semibold text-[#009fe3]/90 mb-3">
                      {step.tagline}
                    </div>

                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      {step.detail}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between text-xs">
                    <span className="text-slate-500">Rigorous milestone sign-off</span>
                    <span className="text-[#009fe3] font-mono">Ready →</span>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>
    </>
  );
};
